import type { Preferences } from "../api/types.js";
import type { ReadAloudMode } from "../app/read-aloud.js";
import { el } from "../lib/dom.js";
import { READ_ALOUD_VALUE } from "./read-aloud-sheet.js";
import { VOICE_CONSENT } from "../app/voice-consent.js";
import { icon, type IconId } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * Me (UX spec v3 §6.14 `off`, mock-up 12-judge.html, copy §8.3): a customer
 * screen. A header (avatar, name, the passkey's state) and settings rows where the
 * whole row is the control; a switch row is `role="switch"` with `aria-checked`
 * on the row. Nothing here names a hash, a rule or a model.
 *
 * Built honestly for this build (README deviations):
 *  - DCTA-162: Read aloud is a row with its value (When I speak · Always · Never) that opens its sheet (F29); the
 *    bank's voice comes from DCTA-161's route — until it answers, nothing is read aloud, whatever the choice;
 *  - the Passkey row is information, not a control (nothing opens from it yet);
 *  - Judge mode exists only where the deployment runs in demo mode;
 *  - a switch flips at once (it is the customer's own setting) and goes back, with
 *    one line, when the bank did not keep it.
 */
export type PreferenceKey = "large_type" | "notifications";

export interface MeState {
  readonly name: string;
  readonly initials: string;
  readonly passkeys: number;
  readonly preferences: Preferences;
  /** DCTA-162 F29: the Read aloud choice (`app/read-aloud-setting.ts` reads it from the bank and this device). */
  readonly readAloud: ReadAloudMode;
  readonly demoMode: boolean;
  readonly judge: boolean;
  /** DCTA-176 (F48): the identity check is not up to date — "Your identity check" opens the refresh (`kyc-model.ts › kycRow`). */
  readonly kyc?: { readonly sub: string } | undefined;
}

/** The screens Me opens (round E batch 1, DCTA-111): 17 Payees, 19 Security; DCTA-154: Limits. */
export type MeDestination = "payees" | "security" | "limits";

export interface MeHandlers {
  onSwitch(key: PreferenceKey, on: boolean): void;
  onOpen(where: MeDestination): void;
  /** DCTA-162 F29: the Read aloud row opens its sheet. */
  onReadAloud(): void;
  onJudge(on: boolean): void;
  /** DCTA-176 (F48): the "Your identity check" row — the same refresh as the chat's chip and Home's banner. */
  onKyc?(): void;
  /** DCTA-216 (W11): Me › Help › How it works — the first-run walkthrough again, at step 1. */
  onHowItWorks?(): void;
  onSignOut(): void;
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
}

export class MeView {
  readonly parts: readonly HTMLElement[];
  readonly topbar: HTMLElement;
  readonly body: HTMLElement;
  readonly dock: HTMLElement;
  readonly mark: HTMLButtonElement;
  /** Where judge mode's surface goes, under the list (12 `on`). */
  readonly judgeSlot: HTMLElement;
  /** DCTA-216: Help › How it works — focus comes back here when the walkthrough it opened closes. */
  readonly howRow: HTMLButtonElement;
  readonly #h: MeHandlers;
  readonly #head: HTMLElement;
  readonly #list: HTMLElement;
  readonly #out: HTMLElement;
  readonly #banner: HTMLElement;
  readonly #switches = new Map<string, HTMLElement>();
  #readAloudValue: HTMLElement | undefined;

  constructor(handlers: MeHandlers) {
    this.#h = handlers;
    this.topbar = el("div", { class: "topbar" }, [
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
      el("span", { class: "t" }, ["Me"]),
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
    ]);
    this.#banner = el("div", { class: "banner", role: "status", hidden: true }, [
      el("span", { class: "bic", aria: { hidden: "true" } }, [icon("i-alert")]),
      el("span", {}, ["Couldn't save that. Nothing has changed."]),
    ]);
    this.#head = el("div", { class: "mehead" });
    this.#list = el("div", { class: "list settings" });
    // DCTA-216 (10-welcome.md §0.2, W11): Help, a section of its own; Sign out stands alone after it (shared.css §14.3).
    this.howRow = el("button", { class: "raw setrow wlrow", type: "button", aria: { haspopup: "dialog" } }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, ["?"]),
      el("span", { class: "tx" }, [el("b", {}, ["How it works"]), el("small", {}, ["Start the assistant, say it, sign it"])]),
      icon("i-chev-r", "chev"),
    ]);
    this.howRow.addEventListener("click", () => this.#h.onHowItWorks?.());
    this.#out = el("div", { class: "list settings" });
    this.judgeSlot = el("div", { class: "judge-slot" });
    this.body = el("div", { class: "body" }, [
      el("div", { class: "mev" }, [
        this.#head,
        this.#banner,
        this.#list,
        el("div", { class: "sec" }, [el("h3", {}, ["Help"])]),
        el("div", { class: "list settings" }, [this.howRow]),
        this.#out,
        this.judgeSlot,
        el("div", { class: "home-end", aria: { hidden: "true" } }),
      ]),
    ]);
    const bar = tabBar("me", { onTab: (t) => this.#h.onTab(t), ...this.#h.assistant });
    this.mark = bar.mark;
    this.dock = bar.dock;
    this.parts = [this.topbar, this.body, this.dock];
  }

  show(s: MeState): void {
    this.#banner.hidden = true;
    this.#head.replaceChildren(
      el("div", { class: "avatar", aria: { hidden: "true" } }, [s.initials]),
      el("div", { class: "tx" }, [
        el("b", {}, [s.name]),
        ...(s.passkeys > 0 ? [el("small", {}, ["Passkey ready · synced across your devices"])] : []),
      ]),
    );
    this.#switches.clear();
    const kyc = s.kyc;
    const rows: HTMLElement[] = [
      // DCTA-176 (F48): while the identity check is not up to date, its row comes first — the way to update it from here.
      ...(kyc === undefined || this.#h.onKyc === undefined ? [] : [this.#kycRow(kyc.sub)]),
      // DCTA-111 (round E batch 1): the screens Me opens come first, a chevron each; copy flagged for ux-designer.
      this.#nav("payees", "i-users", "Payees", "Who you pay, by name and number"),
      // DCTA-154: read only — where a payment's cap refusal points (backend DCTA-153 ruling 3); copy flagged for ux-designer.
      this.#nav("limits", "i-sliders", "Limits", "How much can go out, and when"),
      this.#nav("security", "i-shield", "Security", "Lock your money · your passkeys"),
      info("i-passkey", "Passkey", s.passkeys > 0 ? "Ready · one touch to sign" : "Made when you first sign"),
      this.#switch("large_type", glyph("Aa"), "Large type", "Bigger text and rows, no springs", s.preferences.large_type, (on) =>
        this.#h.onSwitch("large_type", on),
      ),
      this.#readAloudRow(s.readAloud),
      // Claude's ruling 2 (13 Oct, DCTA-198): Me › Privacy — what is kept of a spoken turn, as information.
      info("i-mic", "Privacy", VOICE_CONSENT),
      this.#switch("notifications", icon("i-bell"), "Notifications", "When something goes out or is held", s.preferences.notifications, (on) =>
        this.#h.onSwitch("notifications", on),
      ),
    ];
    if (s.demoMode) {
      const judge = this.#switch("judge", icon("i-badge"), "Judge mode", "For the judging panel · shows how each payment was checked", s.judge, (on) =>
        this.#h.onJudge(on),
      );
      judge.classList.add("judge-switch");
      rows.push(judge);
    }
    const out = el("button", { class: "raw setrow", type: "button" }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-lock")]),
      el("span", { class: "tx" }, [el("b", {}, ["Sign out"]), el("small", {}, ["Ends this session · what you signed still goes ahead"])]),
      icon("i-chev-r", "chev"),
    ]);
    out.addEventListener("click", () => this.#h.onSignOut());
    this.#list.replaceChildren(...rows);
    this.#out.replaceChildren(out);
  }

  /** DCTA-162 F29: the row's value follows the choice (made on the sheet, or put back when the bank did not keep it). */
  readAloud(mode: ReadAloudMode): void {
    const value = this.#readAloudValue;
    if (value !== undefined) value.textContent = READ_ALOUD_VALUE[mode];
  }

  /** The bank did not keep a change: the switch goes back, and one line says so. */
  revert(key: string, on: boolean): void {
    this.#switches.get(key)?.setAttribute("aria-checked", on ? "true" : "false");
    this.#banner.hidden = false;
  }

  /** Judge mode's switch as the surface under it stands (12 `off` / `on`). */
  judge(on: boolean): void {
    this.#switches.get("judge")?.setAttribute("aria-checked", on ? "true" : "false");
  }

  /**
   * DCTA-162 F29 (spoken.md §1.2): "Read aloud" with its value on the right — a row that opens the three choices (the
   * design's right-hand value, `.setrow .rt`, and a chevron). Its name is the title and the value ("Read aloud When I
   * speak"); it opens a dialog.
   */
  #readAloudRow(mode: ReadAloudMode): HTMLElement {
    const value = el("span", { class: "rt" }, [READ_ALOUD_VALUE[mode]]);
    this.#readAloudValue = value;
    const row = el("button", { class: "raw setrow", type: "button", aria: { haspopup: "dialog" } }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-speaker")]),
      el("span", { class: "tx" }, [el("b", {}, ["Read aloud"])]),
      value,
      icon("i-chev-r", "chev"),
    ]);
    row.addEventListener("click", () => this.#h.onReadAloud());
    return row;
  }

  #kycRow(sub: string): HTMLElement {
    const row = el("button", { class: "raw setrow kycrow", type: "button" }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-id")]),
      el("span", { class: "tx" }, [el("b", {}, ["Your identity check"]), el("small", {}, [sub])]),
      icon("i-chev-r", "chev"),
    ]);
    row.addEventListener("click", () => this.#h.onKyc?.());
    return row;
  }

  #nav(where: MeDestination, ic: IconId, title: string, sub: string): HTMLElement {
    const row = el("button", { class: "raw setrow", type: "button" }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon(ic)]),
      el("span", { class: "tx" }, [el("b", {}, [title]), el("small", {}, [sub])]),
      icon("i-chev-r", "chev"),
    ]);
    row.addEventListener("click", () => this.#h.onOpen(where));
    return row;
  }

  #switch(key: string, ic: Element, title: string, sub: string, on: boolean, flip: ((on: boolean) => void) | undefined): HTMLElement {
    const row = el("button", { class: "raw setrow", type: "button", role: "switch", aria: { checked: on ? "true" : "false" } }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [ic]),
      el("span", { class: "tx" }, [el("b", {}, [title]), el("small", {}, [sub])]),
      el("span", { class: "sw", aria: { hidden: "true" } }),
    ]);
    if (flip === undefined) row.setAttribute("aria-disabled", "true");
    row.addEventListener("click", () => {
      if (flip === undefined) return;
      const next = row.getAttribute("aria-checked") !== "true";
      row.setAttribute("aria-checked", next ? "true" : "false");
      this.#banner.hidden = true;
      flip(next);
    });
    this.#switches.set(key, row);
    return row;
  }
}

function info(id: IconId, title: string, sub: string): HTMLElement {
  return el("div", { class: "setrow" }, [
    el("span", { class: "ic", aria: { hidden: "true" } }, [icon(id)]),
    el("span", { class: "tx" }, [el("b", {}, [title]), el("small", {}, [sub])]),
  ]);
}

function glyph(text: string): HTMLElement {
  return el("span", {}, [text]);
}
